import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Search, Check } from "lucide-react";

const LAYOUTS = [
  { key: "4x2", label: '4×2 (A4 portrait)', cols: 2, rows: 4 },
  { key: "3x3", label: '3×3 (square sheet)', cols: 3, rows: 3 },
  { key: "2x1", label: '2×1 (large bin tags)', cols: 1, rows: 2 },
];

const Labels = () => {
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState({});
  const [search, setSearch] = useState("");
  const [layout, setLayout] = useState("4x2");

  useEffect(() => {
    api.get("/stock").then((r) => setItems(r.data)).catch(() => toast.error("Load failed"));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.name.toLowerCase().includes(q) ||
        (it.sku || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const pickedItems = items.filter((i) => picked[i.id]);
  const lay = LAYOUTS.find((l) => l.key === layout) || LAYOUTS[0];

  const pickAll = () => setPicked(Object.fromEntries(filtered.map((i) => [i.id, true])));
  const pickNone = () => setPicked({});

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="labels-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 print:hidden">
        <div>
          <div className="label-eyebrow">QR shelf labels · printable A4</div>
          <h1 className="font-display text-5xl leading-none mt-2">Labels</h1>
        </div>
        <button
          data-testid="labels-print"
          disabled={pickedItems.length === 0}
          onClick={() => window.print()}
          className="nb-btn nb-btn-primary px-5 py-3 flex items-center gap-2"
        >
          <Printer size={18} strokeWidth={2.5} /> Print ({pickedItems.length})
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 print:hidden">
        <div className="nb-card p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg">Pick items</h2>
            <div className="flex gap-1">
              <button
                data-testid="labels-pick-all"
                onClick={pickAll}
                className="nb-btn bg-white px-2 py-1 text-xs"
              >
                All
              </button>
              <button
                data-testid="labels-pick-none"
                onClick={pickNone}
                className="nb-btn bg-white px-2 py-1 text-xs"
              >
                None
              </button>
            </div>
          </div>
          <div className="relative mb-3">
            <Search
              size={18}
              strokeWidth={2.5}
              className="absolute left-3 top-1/2 -translate-y-1/2"
            />
            <input
              data-testid="labels-search"
              className="nb-input pl-11 h-11"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1 max-h-[480px] overflow-y-auto scrollbar-clean pr-1">
            {filtered.map((it) => {
              const on = !!picked[it.id];
              return (
                <button
                  key={it.id}
                  data-testid={`labels-pick-${it.id}`}
                  onClick={() => setPicked((p) => ({ ...p, [it.id]: !on }))}
                  className="flex items-center gap-2 p-2 border-2 rounded-lg text-left"
                  style={{
                    borderColor: on ? "#228EBF" : "#001740",
                    background: on ? "#DFEEF7" : "#fff",
                  }}
                >
                  <div className="w-5 h-5 rounded border-2 border-black flex items-center justify-center bg-white">
                    {on && <Check size={12} strokeWidth={3} className="text-[#228EBF]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm truncate">{it.name}</div>
                    <div className="label-eyebrow">
                      {it.sku || "no SKU"} · {it.type}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 nb-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg">Layout</h2>
            <div className="flex gap-2">
              {LAYOUTS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`labels-layout-${l.key}`}
                  onClick={() => setLayout(l.key)}
                  className={`nb-btn px-3 py-1.5 text-xs ${
                    layout === l.key ? "nb-btn-primary" : "bg-white"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {pickedItems.length === 0 ? (
            <div className="text-sm text-[#607087] py-12 text-center">
              Pick items on the left to preview labels.
            </div>
          ) : (
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(${lay.cols}, minmax(0, 1fr))` }}
              data-testid="labels-preview"
            >
              {pickedItems.map((it) => (
                <LabelTile key={it.id} item={it} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Print sheet */}
      <div className="hidden print:block">
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${lay.cols}, minmax(0, 1fr))`,
          }}
        >
          {pickedItems.map((it) => (
            <LabelTile key={it.id} item={it} />
          ))}
        </div>
      </div>
    </div>
  );
};

const LabelTile = ({ item }) => {
  // Encode the QR as a deep-link URL that opens the app straight into
  // Take Stock with the item pre-selected. Any phone camera scanning the
  // label (staff, presentation demo, etc.) will land on the correct
  // screen — bare SKU/UUID strings just get sent to Google by the phone's
  // default browser handler, which is what caused the "did not match any
  // documents" 404 during the demo. In-app scanner still handles raw SKUs
  // from supplier barcodes because handleScan() extracts the ?scan= param
  // if it detects a URL.
  const sku = item.sku || item.id;
  const value = `${window.location.origin}/stock-out?scan=${encodeURIComponent(sku)}`;
  return (
    <div
      className="border-2 border-black rounded-lg p-3 flex items-center gap-3 bg-white"
      data-testid={`label-tile-${item.id}`}
    >
      <QRCodeSVG value={value} size={128} bgColor="#FFFFFF" fgColor="#001740" level="M" />
      <div className="flex-1 min-w-0">
        <div className="font-display text-base leading-tight">{item.name}</div>
        <div className="label-eyebrow mt-1">{item.sku || "—"}</div>
        <div className="label-eyebrow">
          {item.type} {item.size_spec ? `· ${item.size_spec}` : ""}
        </div>
        {item.bin_location && (
          <div className="font-mono-num font-bold text-sm mt-1">{item.bin_location}</div>
        )}
      </div>
    </div>
  );
};

export default Labels;
